import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, ScrollText, Scale, Gavel, BookOpen } from "lucide-react";

export default function AboutSection() {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        {/* Left: elegant crest / monogram panel (no photo) */}
        <div className="relative">
          <div className="absolute -top-4 -left-4 w-32 h-32 border-4 border-[#c9a961] rounded-sm z-0" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-[#0e1b34] rounded-sm z-0" />

          <div
            className="relative z-10 rounded-sm overflow-hidden h-[560px] flex flex-col items-center justify-center text-center px-8"
            style={{
              background:
                "linear-gradient(160deg, #0e1b34 0%, #14264a 55%, #0e1b34 100%)",
            }}
          >
            {/* subtle radial highlights */}
            <div
              className="absolute inset-0 pointer-events-none opacity-40"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 25% 20%, rgba(201,169,97,0.28) 0, transparent 45%), radial-gradient(circle at 80% 85%, rgba(201,169,97,0.18) 0, transparent 45%)",
              }}
            />

            {/* Crest */}
            <div className="relative z-10 w-40 h-40 rounded-full border-2 border-[#c9a961] flex items-center justify-center mb-6 bg-[#0a1428]/40 backdrop-blur-sm">
              <div className="w-32 h-32 rounded-full border border-[#c9a961]/50 flex items-center justify-center">
                <Scale className="text-[#c9a961]" size={56} />
              </div>
            </div>

            <div className="relative z-10">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#c9a961] font-semibold">
                Est. 2004
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white mt-3 leading-tight">
                Pravin Gupta
                <br />
                <span className="text-[#c9a961]">&amp; Associates</span>
              </h3>
              <div className="mt-4 h-[1px] w-16 bg-[#c9a961] mx-auto" />
              <p className="text-neutral-300 text-sm mt-4 italic max-w-sm">
                &ldquo;Justice delivered with integrity, ethics and the discipline of law.&rdquo;
              </p>
            </div>

            {/* Bottom pillars */}
            <div className="relative z-10 mt-8 grid grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Gavel className="text-[#c9a961] mb-2" size={20} />
                <div className="text-[10px] uppercase tracking-widest text-neutral-300">
                  Litigation
                </div>
              </div>
              <div className="flex flex-col items-center">
                <BookOpen className="text-[#c9a961] mb-2" size={20} />
                <div className="text-[10px] uppercase tracking-widest text-neutral-300">
                  Advisory
                </div>
              </div>
              <div className="flex flex-col items-center">
                <ScrollText className="text-[#c9a961] mb-2" size={20} />
                <div className="text-[10px] uppercase tracking-widest text-neutral-300">
                  Drafting
                </div>
              </div>
            </div>
          </div>

          {/* Floating badge */}
          <div className="absolute -bottom-6 left-6 right-6 z-20 bg-white shadow-xl border border-neutral-100 px-5 py-4 rounded-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-[#0e1b34] flex items-center justify-center flex-shrink-0">
              <Award className="text-[#c9a961]" size={20} />
            </div>
            <div>
              <div className="font-serif-display text-[#0e1b34] font-semibold text-lg leading-tight">
                20+ Years of Excellence
              </div>
              <div className="text-xs text-neutral-600">
                Trusted legal representation since 2004
              </div>
            </div>
          </div>
        </div>

        {/* Right: content */}
        <div className="lg:pl-6">
          <span className="text-[#b28e42] uppercase tracking-[0.35em] text-xs font-semibold">
            About Us
          </span>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#0e1b34] mt-3 mb-2 leading-tight gold-underline">
            Pravin Gupta &amp; Associates.
          </h2>
          <h3 className="font-serif-display text-lg text-[#b28e42] italic mt-6 mb-6">
            Advocates &amp; Solicitors - Serving clients across Mumbai and India since 2004.
          </h3>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Founded and established in 2004 by dynamic Advocate &amp; Solicitor Mr. Pravin K. Gupta with a belief
            to provide quick and valuable solutions across various legal segments with a strong emphasis on
            quality and ethics.
          </p>
          <p className="text-neutral-700 leading-relaxed mb-4">
            Our team provides services in the legal field at domestic as well as international levels. Our prime
            focus is to simplify legal complications and hurdles of our clients by adopting the best possible ways
            in accordance with prevailing laws. Our legal brains provide the result of any legal complications of
            our clients at the blink of an eye.
          </p>
          <p className="text-neutral-700 leading-relaxed mb-8">
            Our young and dynamic legal eagles not only remain updated with the recent legal amendments but also
            stay a step ahead of the others by adopting and using the latest technology.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex items-start gap-3">
              <ScrollText className="text-[#c9a961] mt-0.5" size={20} />
              <div>
                <div className="text-[#0e1b34] font-semibold">Ethical Practice</div>
                <div className="text-xs text-neutral-600">Client-first, always</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Award className="text-[#c9a961] mt-0.5" size={20} />
              <div>
                <div className="text-[#0e1b34] font-semibold">Proven Results</div>
                <div className="text-xs text-neutral-600">5000+ loyal clients</div>
              </div>
            </div>
          </div>

          <Link to="/about" className="btn-gold">
            Read More <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
